import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { PRIORITY_META, CONTROL_STATUS_META } from "../contexts/ComplianceContext";
import { Search } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function ControlsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fw, setFw] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (fw) params.set("framework_id", fw);
      if (status) params.set("status", status);
      if (priority) params.set("priority", priority);
      const { data } = await api.get(`/compliance/controls?${params.toString()}`);
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [q, fw, status, priority]);

  useEffect(() => {
    load();
  }, [fw, status, priority, load]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/compliance/frameworks");
      setFrameworks(data.items || []);
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Controls Library</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cross-framework catalog of security controls. Each control can satisfy multiple frameworks.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="controls-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="pl-9"
                placeholder="Search controls…"
              />
            </div>
            <select
              value={fw}
              onChange={(e) => setFw(e.target.value)}
              data-testid="controls-framework-filter"
              className="h-10 w-full sm:w-56 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">All frameworks</option>
              {frameworks.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="controls-status-filter"
              className="h-10 w-full sm:w-44 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">All statuses</option>
              {Object.entries(CONTROL_STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              data-testid="controls-priority-filter"
              className="h-10 w-full sm:w-40 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">All priorities</option>
              {Object.entries(PRIORITY_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <Button variant="outline" onClick={load} data-testid="controls-search-btn">Search</Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2.5 px-4 label-eyebrow">Control</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Department</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Owner</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Frameworks</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Priority</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Status</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Frequency</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No controls found.</td></tr>
                )}
                {items.map((c) => {
                  const s = CONTROL_STATUS_META[c.status] || { label: c.status, variant: "neutral" };
                  const p = PRIORITY_META[c.priority] || { label: c.priority, variant: "neutral" };
                  return (
                    <tr
                      key={c.id}
                      data-testid={`control-row-${c.id}`}
                      className="border-b border-border hover:bg-muted/40 transition-colors cursor-pointer"
                      onClick={() => navigate(`/controls/${c.id}`)}
                    >
                      <td className="py-3 px-4 font-medium max-w-md">
                        <div className="truncate">{c.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.description}</div>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{c.department || "—"}</td>
                      <td className="py-3 px-4 text-muted-foreground">{c.owner_name || "—"}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {(c.mappings || []).map((m) => {
                            const f = frameworks.find((x) => x.id === m.framework_id);
                            return (
                              <Badge key={`${m.framework_id}-${m.control_ref}`} variant="primary" className="font-mono">
                                {f?.code || "?"}
                              </Badge>
                            );
                          })}
                        </div>
                      </td>
                      <td className="py-3 px-4"><Badge variant={p.variant}>{p.label}</Badge></td>
                      <td className="py-3 px-4"><Badge variant={s.variant}>{s.label}</Badge></td>
                      <td className="py-3 px-4 text-muted-foreground capitalize">
                        {c.assessment_frequency?.replace("_", " ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
